import React, { useCallback, useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { isElement } from '@shared/helpers/recursive-element.helpers';
import { IElement, OneOfListElements } from '@shared/models/project.model';
import { useAddStandardElement } from '@client/store/project';
import { AddCircle } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { calculateIndentation } from '@client/hooks/use-nested-elements-list.hooks';
import {
  isElementWithGeneratedChildren,
  isGenerated,
} from '@shared/helpers/element.helpers';
import amplitudeLog from '@client/amplitude';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import { useIsElementExpanded } from '@client/hooks/use-expand-elements.hooks';
import { useSortElementsBy } from '@client/hooks/use-filter-elements.hooks';
import { ElementCategoryMenu } from '@features/elements/components/ElementCategoryMenu/ElementCategoryMenu';
import { useGroupedElementCategories } from '@features/elements/hooks/use-grouped-element-categories.hook';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import { IAddElementOptions } from '@shared/helpers/project.helpers';

interface CreateSiblingButtonProps {
  element: IElement;
  indentation: number;
}

const CreateSiblingButton: React.FC<CreateSiblingButtonProps> = ({
  element,
  indentation,
}) => {
  const addElement = useAddStandardElement();
  const { classes } = useStyles();
  const categoryGroups = useGroupedElementCategories();
  const generated = isGenerated(element);
  const hasGeneratedChildren = isElementWithGeneratedChildren(element);
  const expanded = useIsElementExpanded(element);
  const sortBy = useSortElementsBy();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const openMenu = useCallback(
    (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget),
    [],
  );

  const closeMenu = useCallback(() => setAnchorEl(null), []);

  const handleAddElement = useCallback(
    (categoryId: ElementCategoryID) => {
      const options: IAddElementOptions = {
        isSibling: !expanded,
        placement: 'after',
      };

      amplitudeLog('Element New', {
        Place: 'Between',
      });

      addElement(element, categoryId, options);
    },
    [expanded, element, addElement],
  );

  const containerStyles = useMemo(
    () => ({
      left: `${calculateIndentation(indentation)}%`,
      width: `${100 - calculateIndentation(indentation)}%`,
    }),
    [indentation],
  );

  if (
    generated ||
    (hasGeneratedChildren && expanded) ||
    sortBy !== 'position'
  ) {
    return null;
  }

  return (
    <div className={classes.container} style={containerStyles}>
      <span>
        <button className={classes.button} onClick={openMenu}>
          <Tooltip title="New element" disableInteractive>
            <AddCircle
              color="secondary"
              className={classes.addIcon}
              style={iconStyles}
            />
          </Tooltip>
        </button>
        <ElementCategoryMenu
          anchor={anchorEl}
          categoryGroups={categoryGroups}
          onSelect={handleAddElement}
          onClose={closeMenu}
        />
      </span>
    </div>
  );
};

const iconStyles = {
  left: 56,
};

const useStyles = makeStyles()(({ spacing, palette }) => ({
  container: {
    width: '100%',
    height: spacing(1),
    bottom: 0,
    position: 'absolute',
    zIndex: 2,
    '& span': {
      opacity: 0,
      transition: '250ms',
    },
    '&:hover span': {
      opacity: 1,
    },
    '&:drag': {
      display: 'none',
    },
    '&:-webkit-drag': {
      display: 'none',
    },
  },
  button: {
    width: 'fill-available',
    border: 0,
    height: spacing(1),
    position: 'absolute',
    cursor: 'pointer',
    backgroundColor: palette.secondary.main,
  },
  addIcon: {
    position: 'absolute',
    top: spacing(-2.1),
    borderRadius: '100%',
    cursor: 'pointer',
    backgroundColor: palette.background.paper,
  },
}));

export const useShouldShowCreateSiblingButton = (
  element: OneOfListElements,
) => {
  const readonly = useIsReadonly();

  return !readonly && isElement(element);
};

export default CreateSiblingButton;
